import { useEffect, useState } from 'react';
import { X, Loader2, Copy, MessageSquare, Trash2, Share2 } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import { buildBrandedQrPng } from '@/lib/brandedQr';

/**
 * DhfShareModal — patient mints & manages a one-time share link they can
 * WhatsApp / show as a QR to another doctor. Zero SMS cost to DocSite:
 * the patient's own phone forwards the link; the receiving doctor pays
 * their own OTP if they choose to onboard.
 *
 * Ethical rails:
 *   • Only the patient (holder of the DHF viewer URL) can mint.
 *   • Max 3 mints per file per 24h; each token single-use, 24-hr TTL.
 *   • Receiving doctor MUST OTP-verify their own mobile before unlock.
 *   • Received DHFs live in a read-only inbox — never auto-fill their
 *     composer (see /api/dashboard/health-file/last-for-patient guardrail).
 */
export default function DhfShareModal({ viewToken, patientName, doctorName, onClose }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [share, setShare] = useState(null); // { share_token, share_url, wa_url, expires_at }
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [activeShares, setActiveShares] = useState([]);

  const loadShares = async () => {
    try {
      const { data } = await api.get(`/public/dhf/${viewToken}/shares`);
      setActiveShares(data.active || []);
    } catch (_) { /* silent */ }
  };
  useEffect(() => { loadShares(); }, [viewToken]);

  const mint = async () => {
    setErr(''); setBusy(true);
    try {
      const { data } = await api.post(`/public/dhf/${viewToken}/share-mint`);
      setShare(data);
      const png = await buildBrandedQrPng(data.share_url, {
        topHeader: 'Share this DHF · 24h · single-use',
        benefit: 'Your doctor scans this to open your Digital Health File',
        label: doctorName ? `Referral from ${doctorName}` : '',
        attribution: 'Powered by DocSite Platform™',
      });
      setQrDataUrl(png);
      loadShares();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || 'Could not create share link');
    } finally { setBusy(false); }
  };

  const revoke = async (share_token) => {
    try {
      await api.post(`/public/dhf/share/${share_token}/revoke`);
      if (share?.share_token === share_token) { setShare(null); setQrDataUrl(''); }
      loadShares();
    } catch (_) { /* ignore */ }
  };

  const copyUrl = async () => {
    if (!share?.share_url) return;
    try { await navigator.clipboard.writeText(share.share_url); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_) { /* clipboard blocked */ }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="dhf-share-modal">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-4 border-b border-slate-100">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Share with a doctor</p>
            <h3 className="text-base font-bold text-slate-900 mt-0.5">Send this DHF via QR or link</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">Single-use · 24-hour expiry · zero SMS cost</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="dhf-share-close"><X size={18} /></button>
        </div>

        <div className="p-4 space-y-3">
          {!share && (
            <>
              <p className="text-[12px] text-slate-600 leading-relaxed">
                Tap below to create a fresh share link. You can then WhatsApp it or show the QR to the receiving doctor — <b>they OTP-verify on their own phone</b> before your file opens for them.
              </p>
              {err && <p className="text-[11px] text-rose-600">{err}</p>}
              <button onClick={mint} disabled={busy}
                className="w-full py-2.5 rounded-xl bg-emerald-600 text-white font-bold hover:bg-emerald-700 disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="dhf-share-mint-btn">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Share2 size={15} />}
                {busy ? 'Creating link…' : 'Create share link'}
              </button>
            </>
          )}

          {share && (
            <div className="space-y-3">
              {qrDataUrl && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 flex items-center justify-center">
                  <img src={qrDataUrl} alt="DHF share QR" className="w-52 h-auto" data-testid="dhf-share-qr" />
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <input readOnly value={share.share_url}
                  className="flex-1 rounded-lg border border-slate-200 px-2 py-1.5 text-[11px] bg-slate-50 font-mono"
                  onFocus={(e) => e.target.select()}
                  data-testid="dhf-share-url" />
                <button onClick={copyUrl}
                  className="px-2 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100"
                  data-testid="dhf-share-copy">
                  <Copy size={13} />
                </button>
              </div>
              {copied && <p className="text-[10px] text-emerald-600 -mt-2">Copied.</p>}
              <a href={share.wa_url} target="_blank" rel="noopener noreferrer"
                className="w-full py-2.5 rounded-xl bg-[#25D366] text-white font-bold hover:opacity-90 flex items-center justify-center gap-2"
                data-testid="dhf-share-wa">
                <MessageSquare size={15} /> Forward via WhatsApp
              </a>
              <button onClick={() => revoke(share.share_token)}
                className="w-full py-2 rounded-xl border border-rose-200 text-rose-600 text-xs font-semibold hover:bg-rose-50 flex items-center justify-center gap-1"
                data-testid="dhf-share-revoke-current">
                <Trash2 size={12} /> Revoke this link
              </button>
              <p className="text-[10px] text-slate-400 text-center">Expires {new Date(share.expires_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
            </div>
          )}

          {activeShares.length > 0 && (
            <div className="pt-3 border-t border-slate-100">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Active shares · {activeShares.length}</p>
              <ul className="space-y-1" data-testid="dhf-share-active-list">
                {activeShares.map(s => (
                  <li key={s.share_token} className="flex items-center justify-between gap-2 text-[11px] bg-slate-50 rounded-lg px-2 py-1.5">
                    <span className="text-slate-600 font-mono truncate">{s.share_token.slice(0, 10)}…</span>
                    <span className="text-slate-400 shrink-0">exp {new Date(s.expires_at).toLocaleString('en-IN', { day: 'numeric', month: 'short' })}</span>
                    <button onClick={() => revoke(s.share_token)} className="text-rose-500 hover:text-rose-700" data-testid={`dhf-share-revoke-${s.share_token.slice(0, 6)}`}>
                      <Trash2 size={11} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
